import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Shield,
  Clock,
  FileText,
  RefreshCw,
  X
} from 'lucide-react';

const ComplianceDashboard = () => {
  const [complianceData, setComplianceData] = useState({
    overview: {},
    frameworks: [],
    records: [],
    alerts: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'warning':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  // Load compliance data on component mount
  useEffect(() => {
    loadComplianceData();
  }, []);

  const loadComplianceData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Load overview
      const overviewResponse = await fetch('/compliance/overview', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (overviewResponse.ok) {
        const overviewData = await overviewResponse.json();
        setComplianceData(prev => ({ ...prev, overview: overviewData.overview }));
      }
      
      // Load frameworks
      const frameworksResponse = await fetch('/compliance/frameworks', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (frameworksResponse.ok) {
        const frameworksData = await frameworksResponse.json();
        setComplianceData(prev => ({ ...prev, frameworks: frameworksData.frameworks }));
      }
      
      // Load records
      const recordsResponse = await fetch('/compliance/records', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (recordsResponse.ok) {
        const recordsData = await recordsResponse.json();
        setComplianceData(prev => ({ ...prev, records: recordsData.records }));
      }
      
      // Load alerts
      const alertsResponse = await fetch('/compliance/alerts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (alertsResponse.ok) {
        const alertsData = await alertsResponse.json();
        setComplianceData(prev => ({ ...prev, alerts: alertsData.alerts }));
      }
      
    } catch (error) {
      console.error('Failed to load compliance data:', error);
      setError('Failed to load compliance data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/compliance/reports', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Create and download the report file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to generate report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <XCircle className="w-16 h-16 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Error Loading Data</h3>
        <p className="text-gray-400 mb-4">{error}</p>
        <button
          onClick={loadComplianceData}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Compliance Dashboard</h1>
          <p className="text-gray-400 mt-1">Real-time compliance monitoring and reporting</p>
        </div>
        <button 
          onClick={loadComplianceData}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Overall Compliance</p>
              <p className="text-2xl font-bold text-white">
                {complianceData.overview.compliance_percentage || 0}%
              </p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-green-400">
              <TrendingUp className="w-4 h-4 mr-1" />
              <span>
                {complianceData.overview.compliant || 0} of {complianceData.overview.total_frameworks || 0} compliant
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Active Frameworks</p>
              <p className="text-2xl font-bold text-white">
                {complianceData.overview.total_frameworks || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-blue-400">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>
                {complianceData.overview.compliant || 0} compliant, {complianceData.overview.warning || 0} warnings
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Critical Issues</p>
              <p className="text-2xl font-bold text-white">
                {complianceData.overview.non_compliant || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-green-400">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>
                {complianceData.overview.non_compliant === 0 ? 'No critical issues' : `${complianceData.overview.non_compliant} issues detected`}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Active Alerts</p>
              <p className="text-2xl font-bold text-white">
                {complianceData.alerts.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-purple-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-purple-400">
              <Clock className="w-4 h-4 mr-1" />
              <span>
                {complianceData.alerts.filter(a => a.type === 'critical').length || 0} critical, {complianceData.alerts.filter(a => a.type === 'warning').length || 0} warnings
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Compliance Trends Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Compliance Trends</h2>
        <div className="h-64">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            </div>
          ) : complianceData.records && complianceData.records.length > 0 ? (
            <div className="grid grid-cols-6 gap-4 h-full">
              {complianceData.records.slice(0, 6).map((record, index) => (
                <div key={record.id} className="flex flex-col justify-end">
                  <div className="text-center mb-2">
                    <div className="text-sm text-gray-400">
                      {record.framework_name?.substring(0, 3) || `R${index + 1}`}
                    </div>
                    <div className="text-lg font-bold text-white">
                      {record.compliance_score || 0}%
                    </div>
                  </div>
                  <div 
                    className="bg-gradient-to-t from-blue-600 to-blue-400 rounded-t"
                    style={{ height: `${((record.compliance_score || 0) / 100) * 200}px` }}
                  ></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p>No compliance data available</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Recent Compliance Activity</h2>
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            </div>
          ) : complianceData.records && complianceData.records.length > 0 ? (
            complianceData.records.slice(0, 5).map((record) => (
              <motion.div
                key={record.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center justify-between p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  {getStatusIcon(record.status || 'pending')}
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-medium capitalize">
                        {record.framework_name || 'Compliance Check'}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-blue-400 font-medium">
                        {record.framework_name || 'Framework'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400">
                      {record.last_updated ? new Date(record.last_updated).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-400">Score:</span>
                  <span className="text-lg font-bold text-green-400">
                    {record.compliance_score || 0}%
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status || 'pending')} bg-gray-600`}>
                    {record.status || 'pending'}
                  </span>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p>No compliance records available</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={handleGenerateReport}
            disabled={isLoading}
            className="p-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors text-left"
          >
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Generate Report</div>
                <div className="text-sm text-blue-200">Create compliance summary</div>
              </div>
            </div>
          </button>
          
          <button 
            onClick={() => window.location.href = '/compliance'}
            className="p-4 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-left"
          >
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Run Audit</div>
                <div className="text-sm text-green-200">Start compliance check</div>
              </div>
            </div>
          </button>
          
          <button 
            onClick={() => setShowAlerts(!showAlerts)}
            className="p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-left"
          >
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">View Alerts</div>
                <div className="text-sm text-purple-200">Check notifications</div>
              </div>
            </div>
          </button>
        </div>
      </motion.div>

      {/* Alerts Display */}
      {showAlerts && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Active Alerts</h2>
            <button
              onClick={() => setShowAlerts(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-3">
            {complianceData.alerts && complianceData.alerts.length > 0 ? (
              complianceData.alerts.map((alert, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-l-4 ${
                    alert.type === 'critical' 
                      ? 'bg-red-900/20 border-red-500' 
                      : alert.type === 'warning'
                      ? 'bg-yellow-900/20 border-yellow-500'
                      : 'bg-blue-900/20 border-blue-500'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      alert.type === 'critical' 
                        ? 'bg-red-400' 
                        : alert.type === 'warning'
                        ? 'bg-yellow-400'
                        : 'bg-blue-400'
                    }`}></div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{alert.title}</p>
                      <p className="text-gray-400 text-sm mt-1">{alert.description}</p>
                      <p className="text-gray-500 text-xs mt-2">
                        {alert.timestamp ? new Date(alert.timestamp).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p>No active alerts</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ComplianceDashboard;
